import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { EMPTY, switchMap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzModalService } from 'ng-zorro-antd/modal';
import { BazaNcIntegrationListingsDto } from '@scaliolabs/baza-nc-integration-shared';
import { PurchaseFlowDto, PurchaseFlowSessionDto } from '@scaliolabs/baza-nc-shared';
import { Calculator, IframeIntegrationComponent } from '@scaliolabs/baza-web-ui-components';
import { BazaWebUtilSharedService, StorageService } from '@scaliolabs/baza-web-utils';
import { BazaLoadingService } from '@scaliolabs/baza-core-ng';

import { DocuSignEvents, PatchStartPurchase, PurchaseState, StartPurchase } from '../data-access';

@UntilDestroy()
@Component({
    selector: 'app-purchase-agreement',
    templateUrl: './agreement.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchaseAgreementComponent {
    cart$ = this.store.select(PurchaseState.cart);
    numberOfShares$ = this.store.select(PurchaseState.numberOfShares);
    purchaseStart$ = this.store.select(PurchaseState.purchaseStart);
    recurring$ = this.store.select(PurchaseState.recurring);

    frequency: string;
    i18nBasePath = 'dwpf.agreement';

    constructor(
        private readonly modalService: NzModalService,
        private readonly router: Router,
        private readonly storageService: StorageService,
        private readonly store: Store,
        public readonly loader: BazaLoadingService,
        public readonly wts: BazaWebUtilSharedService,
    ) {
        const calc: Calculator = this.storageService.getObject('calc');

        this.frequency = calc?.frequency;
    }

    public openAgreementModal(entity: BazaNcIntegrationListingsDto, purchaseStart: PurchaseFlowDto) {
        this.refreshSession(entity, purchaseStart, () => {
            this.loadAgreementModal();
        });
    }

    refreshSession(entity: BazaNcIntegrationListingsDto, purchaseStart: PurchaseFlowDto, onSuccess) {
        const purchase: PurchaseFlowSessionDto = {
            offeringId: entity.offeringId,
            numberOfShares: purchaseStart.numberOfShares,
            amount: purchaseStart.amount,
            requestDocuSignUrl: true,
        };

        this.store.dispatch(new StartPurchase(purchase)).subscribe(() => {
            onSuccess();
        });
    }

    loadAgreementModal() {
        const purchaseStart = this.store.selectSnapshot(PurchaseState.purchaseStart);

        const modalRef = this.modalService.create({
            nzClassName: 'ant-modal-transparent',
            nzClosable: false,
            nzMaskClosable: false,
            nzKeyboard: false,
            nzContent: IframeIntegrationComponent,
            nzComponentParams: {
                url: purchaseStart?.docuSignUrl,
                events: [DocuSignEvents.SigningComplete, DocuSignEvents.Decline, DocuSignEvents.Cancel],
                classList: 'iframe-docusign',
            } as Partial<IframeIntegrationComponent<DocuSignEvents>>,
            nzFooter: null,
            nzWidth: 1030,
        });

        modalRef?.componentInstance?.iframeEvent
            .pipe(
                untilDestroyed(this),
                switchMap((currentDocusignLastEvent) => {
                    modalRef.close();

                    if (currentDocusignLastEvent === DocuSignEvents.SigningComplete) {
                        purchaseStart.areDocumentsSigned = true;
                        return this.store.dispatch(new PatchStartPurchase(purchaseStart));
                    }

                    return EMPTY;
                }),
            )
            .subscribe(() => {
                this.navigateToStep('payment');
            });
    }

    public onFormSubmit(): void {
        this.navigateToStep('payment');
    }

    public onBackClick(purchaseStart: PurchaseFlowDto, recurring: boolean): void {
        purchaseStart.fee = null;

        this.store.dispatch(new PatchStartPurchase(purchaseStart)).subscribe(() => {
            if (recurring) {
                this.navigateToStep('details-recurring');
            } else {
                this.navigateToStep('details');
            }
        });
    }

    navigateToStep(url: string) {
        this.router.navigate([this.store.selectSnapshot(PurchaseState.pfConfig)?.pfLink, url], {
            queryParamsHandling: 'preserve',
        });
    }
}
